export const getFromParam = (url: string): string | null => {
  try {
    return new URL(url).searchParams.get("from");
  } catch {
    return null;
  }
};

/**
 * If the URL is a redirect URL (contains `/redirect` with a `dest` query param),
 * extracts and returns the decoded destination path. Otherwise returns null.
 */
export const extractDestFromRedirectUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url, "https://placeholder.com");
    if (parsed.pathname.includes("/redirect") && parsed.searchParams.has("dest")) {
      return decodeURIComponent(parsed.searchParams.get("dest")!);
    }
    return null;
  } catch {
    return null;
  }
};

export const prependBaseUrl = (baseUrl: string, url: string | undefined | null): string => {
  return new URL(url ?? "", baseUrl).toString();
};

export type ComposeViewJobLinkOptions = {
  sourceViewJobLink?: string | null;
  defaultFrom?: string;
};

/** Expects absolute jobClickUrl (e.g. from prependBaseUrl). */
const getJobClickParams = (
  jobClickUrl: string | undefined | null,
  { logOnMissing = true }: { logOnMissing?: boolean } = {},
): { trackingKey: string; encryptedParams: string; adBlob: string; xkcb: string; from: string } => {
  if (!jobClickUrl) {
    if (logOnMissing) {
      console.error("[urlUtils] getJobClickParams: jobClickUrl is missing or falsy");
    }
    return { trackingKey: "", encryptedParams: "", adBlob: "", xkcb: "", from: "" };
  }

  try {
    const urlObject = new URL(jobClickUrl);
    const trackingKey = urlObject.searchParams.get("tk") ?? "";
    const encryptedParams = urlObject.searchParams.get("e") ?? "";
    const adBlob = urlObject.searchParams.get("ad") ?? "";
    const xkcb = urlObject.searchParams.get("xkcb") ?? "";
    const from = urlObject.searchParams.get("from") ?? "";

    return { trackingKey, encryptedParams, adBlob, xkcb, from };
  } catch (e) {
    console.error("[urlUtils] getJobClickParams: failed to parse jobClickUrl", jobClickUrl, e);
    return { trackingKey: "", encryptedParams: "", adBlob: "", xkcb: "", from: "" };
  }
};

export const hasJobClickEncryptedParams = (jobClickUrl: string | undefined | null): boolean => {
  return Boolean(getJobClickParams(jobClickUrl, { logOnMissing: false }).encryptedParams);
};

export const composeViewJobLink = (
  baseUrl: string,
  jobKey: string,
  jobClickUrl: string | undefined | null,
  options: ComposeViewJobLinkOptions = {},
): string => {
  try {
    const { trackingKey, encryptedParams, adBlob, xkcb, from } = getJobClickParams(jobClickUrl, {
      logOnMissing: !options.sourceViewJobLink,
    });

    if (options.sourceViewJobLink) {
      const sourceViewJobUrl = new URL(options.sourceViewJobLink, baseUrl);
      if (encryptedParams) {
        sourceViewJobUrl.searchParams.set("e", encryptedParams);
      }
      const resolvedFrom = from || options.defaultFrom;
      if (resolvedFrom) {
        sourceViewJobUrl.searchParams.set("from", resolvedFrom);
      }
      return sourceViewJobUrl.toString();
    }

    const viewJobUrl = new URL("/m/viewjob", baseUrl);
    viewJobUrl.searchParams.set("jk", jobKey);

    if (trackingKey) {
      viewJobUrl.searchParams.set("tk", trackingKey);
    }
    if (encryptedParams) {
      viewJobUrl.searchParams.set("e", encryptedParams);
    }
    if (adBlob) {
      viewJobUrl.searchParams.set("ad", adBlob);
    }
    if (xkcb) {
      viewJobUrl.searchParams.set("xkcb", xkcb);
    }

    viewJobUrl.searchParams.set("from", from || options.defaultFrom || "hp");

    return viewJobUrl.toString();
  } catch (e) {
    console.error("[urlUtils] composeViewJobLink: failed to compose ViewJobLink", e);
    throw new Error("[urlUtils] Compose ViewJobLink: failed to compose ViewJobLink");
  }
};

export const withFromParam = (url: string, from: string | null | undefined): string => {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("from", from ?? "hp");
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Stamp the RJP impression tracking keys onto a jobClick URL so the click can
 * be attributed back to its rjpImpressions row.
 *
 * rjpImpressions joins clicks on `tk` (== the request-level RJS tracking key,
 * i.e. the GraphQL `findRelevantJobs.trackingKey` / "feedtk") and bridges
 * apply-starts through `rjptk` (the same feedtk, re-emitted on the viewjob page
 * as `mobViewJob.rjptk`). The non-grouped `findRelevantJobs` server path
 * (IndeedJobClickUrlProvider) never writes these params — unlike the grouped
 * homepage path (JobTrackingJobClickUrlProvider) — so a PostApplyAd click
 * arrives with only `ctk` and can't be attributed. We stamp them client-side
 * from the feedtk / ctk we already hold. (RN-3042)
 *
 * Each param is set only when absent, so we never clobber a server-provided
 * value (mirrors prepareJobClickUrl's vjs handling).
 */
export const withRjpTrackingKeys = (
  url: string,
  keys: { tk?: string | null; rjptk?: string | null; mtk?: string | null },
): string => {
  try {
    const parsed = new URL(url);
    const setIfAbsent = (name: string, value: string | null | undefined): void => {
      if (value && !parsed.searchParams.has(name)) {
        parsed.searchParams.set(name, value);
      }
    };
    setIfAbsent("tk", keys.tk);
    setIfAbsent("rjptk", keys.rjptk);
    setIfAbsent("mtk", keys.mtk);
    return parsed.toString();
  } catch {
    return url;
  }
};

const SPONSORED_JOB_URL_PATH = "/pagead/clk";

export function isJobSponsored(jobClickUrl: string | undefined | null): boolean {
  return jobClickUrl?.includes(SPONSORED_JOB_URL_PATH) ?? false;
}

// Prefer the server `sponsored` field (the only correct signal under Project
// Veil's unified /uie/clk URLs); fall back to legacy path parsing when absent.
export function resolveJobSponsored(
  jobClick: { url?: string | null; sponsored?: boolean | null } | null | undefined,
): boolean {
  if (jobClick?.sponsored != null) {
    return jobClick.sponsored;
  }
  return isJobSponsored(jobClick?.url);
}

export const prepareJobClickUrl = (url: string | undefined | null): string | undefined => {
  if (!url) {
    return undefined;
  }

  const secured = url.replace(/^http:\/\//, "https://");
  try {
    const parsed = new URL(secured);
    if (!parsed.searchParams.has("vjs")) {
      parsed.searchParams.set("vjs", "3");
    }
    return parsed.toString();
  } catch {
    return secured;
  }
};
